import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { PageEvent } from '@angular/material/paginator';
import { DataType, DataTypeComponent } from '../../../data-type-component';
import { MyErrorStateMatcher } from '../../../../../../common/field-validation/my-error-state-matcher';
import { DataSource } from '../../../../../views/maintain/data-source/data-source';
import { PageResponseDTO } from '../../../../../../common/paging-dto';
import { DataSourceFactory } from '../../../../../views/maintain/data-source/data-source-factory';
import { EntityGroupDetailsService } from '../../../../../views/entity-group/entity-group-details/entity-group-details.service';
import { FieldConfigDTO } from '../../../../../../system/system-dtos';
import { StringUtils } from '../../../../../../common/string-utils';
import { isObjEmpty } from '../../../../../utils/utils';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  selector: 'app-nested-table-form-field',
  templateUrl: './nested-table-form-field.component.html',
  styleUrls: ['./nested-table-form-field.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false,
})
export class NestedTableFormFieldComponent extends DataTypeComponent implements OnInit, OnChanges {
  @Input() data: any[];

  @Input() fieldConfig: FieldConfigDTO;

  @Input() formGroup: UntypedFormGroup;

  @Input() fields: FieldConfigDTO[];

  @Input() canAdd: boolean;

  @Output() valueChange = new EventEmitter<any[]>();

  private _matTable: MatTable<any>;

  public canEdit: boolean = false;

  public canRemove: boolean = true;

  @ViewChild(MatTable) set setSort(content: ElementRef<MatTable<any>>) {
    if (content) {
      this._matTable = content as any;
    }
  }

  public tableData: MatTableDataSource<any>;

  public displayedFieldsNames: string[];

  public formArray: UntypedFormArray;

  public isInnerMode = false;

  expandedFields: FieldConfigDTO[];

  hiddenFields: FieldConfigDTO[];

  loading = true;

  private dataSource: DataSource;

  pageableData: PageResponseDTO<any>;

  private timestamp: FieldConfigDTO = {
    name: '@timestamp',
    label: 'Timestamp',
    type: DataType.date,
    displayInTable: true,
    dateFormat: 'dd-MM-yy HH:mm:ss.SSS',
  };

  newElementTemplate = {};
  entity: any;

  selectedRows: [] = [];
  selection = new SelectionModel<any>(true, []);

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private dataSourceFactory: DataSourceFactory,
    private route: ActivatedRoute,
    private domSanitizer: DomSanitizer,
    private changeDetector: ChangeDetectorRef,
    private http: HttpClient,
    private entityGroupService: EntityGroupDetailsService
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    if (this.fieldConfig.standaloneTableConfig) {
      this.initializeStandaloneTab();
    } else {
      this.initializeData();
    }
    if (this.fieldConfig.newTableElementUrl) {
      this.http
        .get(StringUtils.convert(this.fieldConfig.newTableElementUrl, this.entity))
        .subscribe((d) => (this.newElementTemplate = d));
    }
  }

  isGameLinkTable(): boolean {
    return this.fieldConfig && this.fieldConfig.name === 'gameLink';
  }

  selectionHasValue(): boolean {
    return this.selection.hasValue();
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.tableData.data.length;
    return numSelected === numRows;
  }

  toggleRow(row: any): void {
    this.selection.toggle(row);
  }

  moveSelectedRowsToTop(): void {
    const data = [...this.tableData.data];
    const selectedSet = new Set(this.selection.selected);
    const selectedRows = data.filter((row) => selectedSet.has(row));
    const notSelectedRows = data.filter((row) => !selectedSet.has(row));

    const newTableOrder = [...selectedRows, ...notSelectedRows];
    this.tableData.data = newTableOrder;

    const rowToOldIndex = new Map<any, number>();
    data.forEach((row, idx) => rowToOldIndex.set(row, idx));

    const oldControls = this.formArray.controls;
    const newControls = newTableOrder.map((row) => {
      const oldIndex = rowToOldIndex.get(row)!;
      return oldControls[oldIndex];
    });

    this.formArray = new UntypedFormArray(newControls);
    this.valueChange.emit(this.formArray.getRawValue());
    this.formGroup.value.gameLink = this.formArray.value;
    this.formGroup.setControl('gameLink', this.formArray);
    this.selection.clear();
    this.changeDetector.detectChanges();
  }

  private initializeStandaloneTab() {
    const { standaloneTableConfig } = this.fieldConfig;
    this.dataSource = this.dataSourceFactory.get(standaloneTableConfig.menuType);
    this.dataSource = this.dataSourceFactory.get(standaloneTableConfig.menuType);
    this.dataSource
      .doGetData(
        standaloneTableConfig,
        this.dataSource.getTabs(standaloneTableConfig)[0],
        { offset: 0, perPage: 50 },
        {},
        this.route.snapshot.params.entityId,
        this.formGroup.getRawValue(),
        {}
      )
      .subscribe((data) => {
        this.standaloneDoOnSubscribe(data);
      });
  }

  setRowData(entity: any) {
    this.entity = entity;
  }

  private standaloneDoOnSubscribe(data) {
    this.pageableData = data;
    this.data = data.content;
    this.initializeData();
  }

  private initializeData() {
    if (!this.data) {
      this.data = [];
    }

    if (!this.canAdd) {
      const data = this.formGroup ? this.formGroup.getRawValue() : {};
      this.canAdd = eval(this.fieldConfig.canAdd);
      this.canEdit = !eval(this.fieldConfig.editReadOnly);
      this.canRemove = eval(this.fieldConfig.canRemove);
    }

    this.isInnerMode = !!this.fields;

    if (this.fieldConfig.standaloneTableConfig) {
      const allFields = this.dataSource
        .getRows(this.fieldConfig.standaloneTableConfig)
        .map((r) => r.columns)
        .reduce((acc, elem) => acc.concat(elem), [])
        .map((c) => c.fields)
        .reduce((acc, elem) => acc.concat(elem), []);

      if (this.fieldConfig.standaloneTableConfig.withTimestamp) {
        allFields.unshift(this.timestamp);
      }

      this.hiddenFields = allFields.filter((f) => f.editHide && f.viewHide);
      this.expandedFields = allFields.filter((f) => !f.displayInTable && !(f.editHide && f.viewHide));
      this.fields = allFields.filter((f) => f.displayInTable);
    } else {
      this.hiddenFields = this.fields ? [] : this.fieldConfig.innerFields.filter((f) => f.editHide && f.viewHide);
      this.expandedFields = this.fields
        ? []
        : this.fieldConfig.innerFields.filter((f) => !f.displayInTable && !(f.editHide && f.viewHide));
      this.fields = this.fields ? this.fields : this.fieldConfig.innerFields.filter((f) => f.displayInTable);
    }

    const addMode = this.router.url.includes('add');
    if (addMode) {
      this.expandedFields = this.expandedFields.filter((f) => !f.addIgnore);
      this.fields = this.fields.filter((f) => !f.addIgnore);
    }

    for (let i = 0; i < this.data.length; i++) {
      this.data[i].index = i;
    }

    if (!this.fieldConfig.standaloneTableConfig) {
      this.fields = this.fieldConfig.nestedTable ? this.fieldConfig.nestedTable : this.fields;
    }

    const formArray = this.fb.array([]);
    this.data.forEach((d) => {
      formArray.push(this.getNewRow(d));
    });
    this.formArray = formArray;

    if (!this.isInnerMode) {
      this.formArray.setValidators(MyErrorStateMatcher.convertToAngularValidators([this.fieldConfig]));
      this.formGroup.setControl(this.fieldConfig.name, formArray);
    }

    this.formArray.valueChanges.subscribe((_) => {
      const raw = this.formArray.getRawValue();
      this.valueChange.emit(raw);
      if (this.tableData?.data?.length) {
        raw.forEach((row, index) => {
          if (this.tableData.data[index]) {
            Object.assign(this.tableData.data[index], row);
          }
        });
        this.changeDetector.markForCheck();
      }
    });

    this.displayedFieldsNames = this.fields.map((f) => f.name);
    this.displayedFieldsNames.push('drag-and-drop');
    this.displayedFieldsNames.push('menu');
    if (this.expandedFields && this.expandedFields.length > 0) {
      this.displayedFieldsNames.unshift('expander');
    }
    this.displayedFieldsNames.unshift('select');
    this.tableData = new MatTableDataSource(formArray.getRawValue());
    this.loading = false;
  }

  private getNewRow(data): UntypedFormGroup {
    const group: UntypedFormGroup = new UntypedFormGroup({});

    const fieldsSet = new Set([
      ...(this.data[0] ? Object.keys(this.data[0]) : ['index']),
      ...this.fields.map((f) => f.name),
      ...this.expandedFields.map((f) => f.name),
      ...this.hiddenFields.map((f) => f.name),
    ]);

    if (this.fieldConfig.reorderWeightField) {
      fieldsSet.add(this.fieldConfig.reorderWeightField);
    }

    fieldsSet.forEach((key) => {
      const value = data[key];
      const fieldsBYName = [...this.fields, ...this.expandedFields].filter((f) => f.name === key);
      const disabled = fieldsBYName.length > 0 ? fieldsBYName[0].editReadOnly : false;
      const validators = MyErrorStateMatcher.convertToAngularValidators(fieldsBYName);
      group.addControl(
        key,
        new UntypedFormControl(
          {
            value,
            disabled,
          },
          validators
        )
      );
    });

    return group;
  }

  isIgnored(field: FieldConfigDTO, data: any) {
    if (field.ignoreIfNotPresent && !this.editMode) {
      const fieldValue = data[field.name];

      return !fieldValue || fieldValue === '' || isObjEmpty(data) || data?.length === 0;
    }

    return false;
  }

  pageChanged(event: PageEvent) {
    this.loading = true;
    this.dataSource
      .doGetData(
        this.fieldConfig.standaloneTableConfig,
        this.dataSource.getTabs(this.fieldConfig.standaloneTableConfig)[0],
        { perPage: event.pageSize, offset: event.pageIndex },
        {},
        this.route.snapshot.params.entityId,
        this.formGroup.getRawValue(),
        {}
      )
      .subscribe((d) => this.standaloneDoOnSubscribe(d));
  }

  transform(style: string | undefined): SafeStyle {
    if (style === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustStyle(style);
  }

  droppedElement($event: CdkDragDrop<any>, row) {
    const { reorderWeightField } = this.fieldConfig;
    const { data } = this.tableData;
    const oldIndex = data.findIndex((el) => el[reorderWeightField] === row[reorderWeightField]);

    moveItemInArray(data, oldIndex, $event.currentIndex);
    moveItemInArray(this.formArray.controls, oldIndex, $event.currentIndex);

    this.reorderData(data, reorderWeightField);
  }

  private reorderData(data: any[], reorderWeightField) {
    for (let i = 0; i < data.length; i++) {
      this.formArray.controls[i].get(reorderWeightField).setValue((i + 1) * 100);
    }
    this.tableData.data = data;
    this._matTable.renderRows();
    this.changeDetector.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {}

  pushOnTop() {
    const newRow = this.getNewRow({ ...this.newElementTemplate, ...{ index: 0 } });
    this.formArray.insert(0, newRow);
    this.formArray.patchValue(this.reindex(this.formArray.getRawValue()));
    this.tableData.data.unshift(newRow.value);
    this.tableData.data.forEach((v, index) => (v.index = index));
    this._matTable.renderRows();
    this.entityGroupService.isotopeLayout.next();
    if (this.fieldConfig.canReorder) {
      for (let i = 0; i < this.tableData.data.length; i++) {
        this.formArray.controls[i].get(this.fieldConfig.reorderWeightField).setValue((i + 1) * 100);
      }
    }
  }

  reindex(array: any[]): any[] {
    let i = 0;
    array.map((value) => (value.index = i++));
    return array;
  }

  appendRow() {
    // this.formArray.push(this.getNewRow({...this.newElementTemplate, ...{index: this.formArray.length}}));
    this.formArray.push(
      this.getNewRow({ ...this.newElementTemplate, ...{ index: this.formArray.length }, displayOrder: 10 })
    );
    this.tableData = new MatTableDataSource(this.formArray.getRawValue());
    this.entityGroupService.isotopeLayout.next();
    if (this.fieldConfig.canReorder) {
      for (let i = 0; i < this.tableData.data.length; i++) {
        this.formArray.controls[i].get(this.fieldConfig.reorderWeightField).setValue((i + 1) * 100);
      }
    }
  }

  deleteRow(row) {
    const rawValue = this.formArray.getRawValue();
    rawValue.splice(row.index, 1);

    for (let i = 0; i < rawValue.length; i++) {
      rawValue[i].index = i;
    }
    this.tableData = new MatTableDataSource(rawValue);
    this.formArray.removeAt(row.index);
  }

  moveTop(row: any) {
    moveItemInArray(this.tableData.data, row.index, 0);
    moveItemInArray(this.formArray.controls, row.index, 0);
    this._matTable.renderRows();
  }

  moveBottom(row: any) {
    moveItemInArray(this.tableData.data, row.index, this.tableData.data.length - 1);
    moveItemInArray(this.formArray.controls, row.index, this.formArray.length - 1);
    this._matTable.renderRows();
  }

  toggleRowExpansion(element) {
    element.expanded = !element.expanded;
    this.entityGroupService.isotopeLayout.next();
  }

  mayBeExpanded(element) {
    return this.expandedFields.filter((e) => !this.isIgnored(e, element)).length > 0;
  }

  actionFinished($event: any) {
    this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
  }

  convertToFormGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }
}
